if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = document.querySelector('#ge-menu-drawer');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this.shell = this.querySelector('[data-site-header]') || this.querySelector('.site-header');
      this._scrollTicking = false;
      this._stickyOffset = 0;
      this._logoCompactThreshold = 48;
      this._lastScrollY = Math.max(window.scrollY || 0, 0);
      this._hideThreshold = 0;
      this._navHidden = false;
      this._headerHero = null;

      this._cacheStickyOffset();
      this._cacheHideThreshold();
      this._hydrateLocalBar();
      this.bindHeaderAtc();
      this.setAnnouncementHeight();
      this.setHeaderOffset();
      this.setHeaderHeight();
      window.addEventListener('resize', debounce(() => {
        this._cacheStickyOffset();
        this._cacheHideThreshold();
        this.setHeaderSurface();
        this.setLogoCompact();
        this.setHeaderHeight();
        this.setLocalState();
        this.setHeaderTone();
      }, 100));

      window.addEventListener('scroll', () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHeaderSurface();
            this.setLogoCompact();
            this.setNavVisibility();
            this.setHeaderOffset();
            this.setHeaderHeight();
            this.setLocalState();
            this.setHeaderTone();
            this._scrollTicking = false;
          });
        }
      }, { passive: true });

      window.dispatchEvent(new Event('scroll'));

      const refreshHeaderContext = () => {
        this._cacheHideThreshold();
        this.setHeaderSurface();
        this.setLogoCompact();
        this.setLocalState();
        this.setHeaderTone();
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', refreshHeaderContext, { once: true });
      } else {
        requestAnimationFrame(refreshHeaderContext);
      }

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('scroll', () => this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

    }

    _cacheStickyOffset() {
      if (this.classList.contains('header-sticky--active')) {
        // Skip recalculation while sticky — the header is position:sticky at top:0,
        // so getBoundingClientRect().top would return 0 and corrupt the offset.
        if (this.classList.contains('is-sticky')) {
          return;
        }
        this._stickyOffset = parseInt(this.header_section.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', Math.max(window.scrollY, 0) >= this._stickyOffset && window.scrollY > 0);
      }
    }
    setLogoCompact() {
      if (!this.shell) return;
      // Wordmark → arch morph on dest hubs (data-logo-morph).
      // Never morph on playlist, item, or commerce detail pages.
      const allowMorph = this.shell.getAttribute('data-logo-morph') === 'true';
      if (!allowMorph) {
        this.shell.classList.remove('is-logo-compact');
        return;
      }
      const mobile = window.matchMedia('(max-width: 989px)').matches;
      if (!mobile || this.shell.classList.contains('is-past-hero')) {
        this.shell.classList.remove('is-logo-compact');
        return;
      }
      // White arch only reads on a transparent bar; a solid (white) bar keeps the ink wordmark.
      const bg = getComputedStyle(this.shell).backgroundColor || '';
      const m = bg.match(/rgba?\(([^)]+)\)/);
      const alpha = m ? (m[1].split(',')[3] !== undefined ? parseFloat(m[1].split(',')[3]) : 1) : 0;
      const transparentBar = bg === 'transparent' || alpha < 0.2;
      const compact = transparentBar && window.scrollY > (this._logoCompactThreshold || 48);
      this.shell.classList.toggle('is-logo-compact', compact);
    }
    _cacheHideThreshold() {
      const hero = document.querySelector(
        '.playlist-detail__hero, .playlist-hero, .hero-banner, [data-header-hero]'
      );

      if (hero) {
        const top = hero.getBoundingClientRect().top + window.scrollY;
        this._logoCompactThreshold = 48;
        this._hideThreshold = top + hero.offsetHeight;
      } else {
        this._logoCompactThreshold = 48;
        this._hideThreshold = Math.round(window.innerHeight * 0.75);
      }
    }
    setNavVisibility() {
      if (!this.shell) return;

      /* Hide-on-scroll runs at every width and on every page (announcement + header as one block). */

      const menuOpen = this.toggle?.classList.contains('active') || this.toggle?.hasAttribute('open');
      // Clamp iOS rubber-band overscroll (negative / past-bottom scrollY) so it can't trigger hide/reveal.
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = Math.min(Math.max(window.scrollY || 0, 0), maxY);
      const last = this._lastScrollY;
      const delta = y - last;
      this._lastScrollY = y;

      if (menuOpen) {
        this._setNavHidden(false);
        return;
      }

      if (y <= (this._hideThreshold || 0)) {
        this._setNavHidden(false);
        return;
      }

      // Ignore tiny jitter; direction drives show/hide past the hero gate.
      if (delta > 6) {
        this._setNavHidden(true);
      } else if (delta < -6) {
        this._setNavHidden(false);
      }
    }
    _setNavHidden(hidden) {
      if (this._navHidden === hidden) return;
      this._navHidden = hidden;
      this.shell.classList.toggle('is-nav-hidden', hidden);
    }
    _hydrateLocalBar() {
      this.local = this.querySelector('[data-header-local]');
      const source = document.querySelector('[data-ge-header-local-source]');
      if (!source || !this.local) return;

      const title = source.getAttribute('data-title');
      const titleEl = this.local.querySelector('[data-header-local-title]');
      if (title && titleEl) titleEl.textContent = title;

      const cta = this.local.querySelector('[data-ge-header-atc]');
      const formId = source.getAttribute('data-form-id');
      if (cta && formId) cta.setAttribute('data-ge-header-form', formId);
      if (cta && source.getAttribute('data-available') === 'false') cta.disabled = true;

      this.local.hidden = false;
      this.shell?.setAttribute('data-header-local', 'true');
    }
    bindHeaderAtc() {
      const cta = this.querySelector('[data-ge-header-atc]');
      if (!cta) return;
      cta.addEventListener('click', (event) => {
        const formId = cta.getAttribute('data-ge-header-form');
        const stickyForm = (formId && document.getElementById(formId)) || document.querySelector('[data-ge-header-sticky-form]');
        if (stickyForm) {
          event.preventDefault();
          if (typeof stickyForm.requestSubmit === 'function') stickyForm.requestSubmit();
          else stickyForm.submit();
          return;
        }
        const productBtn = document.querySelector(
          '.thb-product-detail .single-add-to-cart-button:not([disabled]), product-form .single-add-to-cart-button:not([disabled])'
        );
        if (productBtn) {
          event.preventDefault();
          productBtn.click();
        }
      });
    }
    _canUseLocalBar() {
      if (!this.shell) return false;
      if (!window.matchMedia('(min-width: 990px)').matches) return false;
      if (!document.body.classList.contains('ge-nav-horizontal')) return false;
      return this.shell.getAttribute('data-header-local') === 'true';
    }
    setLocalState() {
      if (!this.shell) return;
      if (!this._canUseLocalBar()) {
        this.shell.classList.remove('is-local');
        return;
      }
      const hero = this._primaryHeaderHero();
      if (!hero) {
        this.shell.classList.remove('is-local');
        return;
      }
      const headerH = this.shell.getBoundingClientRect().height || 48;
      const pastHero = hero.getBoundingClientRect().bottom <= headerH + 8;
      this.shell.classList.toggle('is-local', pastHero);
      if (this.local) this.local.hidden = false;
    }
    _primaryHeaderHero() {
      return (
        document.querySelector('.main-content > .shopify-section:first-child [data-header-hero]') ||
        document.querySelector('[data-header-hero]')
      );
    }
    _headerHeroOverlayActive(hero) {
      if (!hero) return false;
      const mobile = window.matchMedia('(max-width: 989px)').matches;
      const key = mobile ? 'data-header-transparent-mobile' : 'data-header-transparent-desktop';
      return hero.getAttribute(key) !== 'false';
    }
    setHeaderTone() {
      if (!document.body.classList.contains('ge-nav-horizontal')) return;
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      const hero = this._primaryHeaderHero();
      if (hero && !this._headerHeroOverlayActive(hero)) {
        document.body.setAttribute('data-ge-header-tone', 'light');
        return;
      }
      if (
        this.shell?.classList.contains('is-past-hero') &&
        (document.body.classList.contains('ge-page-sunday-reset') ||
          document.body.classList.contains('template-index') ||
          document.body.classList.contains('ge-page-shop'))
      ) {
        document.body.setAttribute('data-ge-header-tone', 'light');
        return;
      }
      if (!this.shell) return;

      const headerRect = this.shell.getBoundingClientRect();
      const x = Math.round(window.innerWidth / 2);
      const y = Math.min(
        window.innerHeight - 4,
        Math.max(4, Math.round(headerRect.top + headerRect.height / 2))
      );
      let tone = null;

      if (typeof document.elementsFromPoint === 'function') {
        const nodes = document.elementsFromPoint(x, y);
        for (const node of nodes) {
          if (!(node instanceof Element)) continue;
          if (node.closest('.header-section, .site-header, .ge-mega, .ge-mega-backdrop')) continue;
          const host = node.closest('[data-header-tone]');
          if (host) {
            tone = host.getAttribute('data-header-tone');
            break;
          }
        }
      }

      if (tone !== 'dark' && tone !== 'light') {
        tone = document.body.classList.contains('ge-header-dark') ? 'dark' : 'light';
      }
      document.body.setAttribute('data-ge-header-tone', tone);
    }
    setHeaderSurface() {
      if (!this.shell) return;
      const supportsHeroTransition =
        document.body.classList.contains('template-index') ||
        document.body.classList.contains('ge-page-sunday-reset') ||
        document.body.classList.contains('ge-page-shop');

      if (!supportsHeroTransition) {
        this.shell.classList.remove('is-past-hero');
        return;
      }

      this._headerHero = this._primaryHeaderHero();
      if (!this._headerHero) {
        this.shell.classList.add('is-past-hero');
        return;
      }

      const headerHeight = this.shell.getBoundingClientRect().height || 48;
      const pastHero = this._headerHero.getBoundingClientRect().bottom <= headerHeight;
      this.shell.classList.toggle('is-past-hero', pastHero);
    }
    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      // Measure the shell: the host element collapses to 0 when the
      // header is position:absolute (transparent over hero pages).
      let h = this.shell ? this.shell.getBoundingClientRect().height : this.clientHeight;
      document.documentElement.style.setProperty('--header-height', Math.round(h) + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;

      // Apple-style accordion: native expand-down — skip Foundation drill-in.
      if (detailsElement?.classList?.contains('ge-mobile-menu__accordion')) {
        return;
      }

      const parentLinksEnabled = this.menu.dataset.parentLinks === 'true';

      // When parent links setting is on, only arrow opens submenu
      if (parentLinksEnabled) {
        event.preventDefault();
        const isArrowClick = event.target.closest('.link-forward');

        if (!isArrowClick) {
          const anchor = summaryElement.querySelector('a');
          if (anchor && anchor.href) {
            window.location.href = anchor.href;
          }
          return;
        }
        // Arrow clicked — manually open details
        detailsElement.setAttribute('open', '');
      }

      const parentMenuElement = detailsElement.closest('.link-container');

      // Stamp deferred template content on first open
      const tpl = detailsElement.querySelector(':scope > template.mobile-menu-deferred');
      if (tpl) {
        tpl.replaceWith(tpl.content.cloneNode(true));
        // Re-bind back buttons for the newly stamped content
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        detailsElement.querySelectorAll('details.link-container summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      }

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      window.addEventListener('resize', debounce(() => {
        this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
